import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { contact, footer as footerContent } from "@/content/site";
import { PageHero, ButtonPrimary } from "@/components/NovaKit";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ENDPOINTS = {
  contact: "/leads/contact",
  setup: "/leads/setup",
  call: "/leads/call",
  partner: "/leads/partner",
};

const FORM_HEADINGS = {
  contact: "Contact Hubinall",
  setup: "Start Your Setup",
  call: "Request a Call",
  partner: "Become a Partner",
};

function useLeadForm(initial, endpoint) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const onChange = (e) => {
    const { name, type, checked, value } = e.target;
    setData((d) => ({ ...d, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoint, { ...data, source_path: window.location.pathname + window.location.search });
      toast.success("Thank you — your request has been received.");
      setData(initial);
    } catch {
      toast.error("Something went wrong. Please email hi@hubinall.com.");
    } finally {
      setLoading(false);
    }
  };
  return { data, onChange, submit, loading, setData };
}

const labelCls = "mono text-[10px] tracking-[0.22em] uppercase text-[#0A0E1A]/60 font-semibold";
const inputCls = "w-full bg-white rounded-xl border hairline px-4 py-3 text-sm focus:border-[#4F46E5]";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className={labelCls}>{label}</span>
      {children}
    </label>
  );
}

function MarketingCheckbox({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid="marketing-consent">
      <input type="checkbox" name="marketing_consent" checked={checked} onChange={onChange} className="mt-1 accent-[#4F46E5]" />
      <span className="text-xs text-[#0A0E1A]/70 leading-relaxed">{footerContent.marketingConsent}</span>
    </label>
  );
}

function PrivacyNotice() {
  return (
    <p className="mono text-[10px] leading-relaxed tracking-wide uppercase text-[#0A0E1A]/55">
      {footerContent.shortPrivacy}
    </p>
  );
}

export default function Contact() {
  const [params, setParams] = useSearchParams();
  const initial = useMemo(() => params.get("form") || "contact", [params]);
  const [mode, setMode] = useState(["contact", "setup", "call", "partner"].includes(initial) ? initial : "contact");

  useEffect(() => { setParams({ form: mode }, { replace: true }); /* eslint-disable-next-line */ }, [mode]);

  const baseInit = { full_name: "", company: "", country: "", email: "", topic: "", message: "", marketing_consent: false };
  const setupInit = { ...baseInit, business_stage: "", support_type: "", preferred_market: "" };
  const callInit = { ...baseInit, interest: "", preferred_datetime: "" };
  const partnerInit = { ...baseInit, partner_type: "" };
  const ctx = useLeadForm(baseInit, ENDPOINTS.contact);
  const setupCtx = useLeadForm(setupInit, ENDPOINTS.setup);
  const callCtx = useLeadForm(callInit, ENDPOINTS.call);
  const partnerCtx = useLeadForm(partnerInit, ENDPOINTS.partner);

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={contact.eyebrow} heading={contact.heading} highlight="next operating layer" intro={contact.intro} index="11" />

      {/* Smart entry options */}
      <section className="border-t hairline panel">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20">
          <div className="mono text-[11px] tracking-[0.25em] uppercase grad-text font-semibold mb-8">11.1 · Smart entry — select what best describes you</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {contact.entryOptions.map(([title, body, form], i) => (
              <button key={title} onClick={() => setMode(form)}
                className={"text-left grad-border bg-white rounded-[20px] border hairline p-6 card-hover group " + (mode === form ? "!border-[#4F46E5]" : "")}
                data-testid={`entry-option-${i}`}>
                <div className="mono text-[10px] tracking-[0.25em] uppercase grad-text font-bold mb-4">0{i + 1}</div>
                <div className="display text-lg md:text-xl font-bold leading-snug mb-3">{title}</div>
                <div className="text-xs leading-relaxed text-[#0A0E1A]/65">{body}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="border-t hairline">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="mono text-[10px] tracking-[0.3em] uppercase grad-text font-semibold mb-5">11.2 · Form</div>
            <h2 className="display text-3xl md:text-5xl font-bold leading-[1] mb-6">{FORM_HEADINGS[mode]}</h2>
            <p className="text-sm md:text-base text-[#0A0E1A]/70 leading-relaxed mb-10">
              Tell us what you are building, coordinating, improving, or exploring. Hubinall will help identify the most relevant path.
            </p>
            <div className="panel rounded-2xl p-6 border hairline">
              <div className="mono text-[10px] tracking-[0.22em] uppercase grad-text font-bold mb-2">Email</div>
              <a href="mailto:hi@hubinall.com" className="display text-xl font-bold hover:text-[#4F46E5]">hi@hubinall.com</a>
            </div>
          </div>

          <div className="lg:col-span-8 bg-white rounded-[24px] border hairline p-8 md:p-10">
            <div className="flex flex-wrap gap-2 mb-8 border-b hairline pb-4" data-testid="form-selector">
              {Object.entries(FORM_HEADINGS).map(([k, v]) => (
                <button key={k} onClick={() => setMode(k)}
                  className={"mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full transition-colors " +
                    (mode === k ? "grad-bg text-white font-semibold" : "text-[#0A0E1A]/70 hover:bg-[#F5F6FA]")}
                  data-testid={`form-tab-${k}`}>
                  {v}
                </button>
              ))}
            </div>

            {mode === "contact" && (
              <form onSubmit={ctx.submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name"><input required name="full_name" value={ctx.data.full_name} onChange={ctx.onChange} className={inputCls}/></Field>
                  <Field label="Company or Firm"><input name="company" value={ctx.data.company} onChange={ctx.onChange} className={inputCls}/></Field>
                  <Field label="Country"><input name="country" value={ctx.data.country} onChange={ctx.onChange} className={inputCls}/></Field>
                  <Field label="Email Address"><input required type="email" name="email" value={ctx.data.email} onChange={ctx.onChange} className={inputCls}/></Field>
                  <Field label="I am contacting Hubinall about">
                    <select name="topic" value={ctx.data.topic} onChange={ctx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {contact.topics.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Message"><textarea rows={4} name="message" value={ctx.data.message} onChange={ctx.onChange} className={inputCls + " resize-none"}/></Field>
                <MarketingCheckbox checked={ctx.data.marketing_consent} onChange={ctx.onChange}/>
                <PrivacyNotice/>
                <ButtonPrimary type="submit" data-testid="submit-contact">{ctx.loading ? "Sending…" : "Send Inquiry"}</ButtonPrimary>
              </form>
            )}

            {mode === "setup" && (
              <form onSubmit={setupCtx.submit} className="space-y-5" data-testid="setup-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name"><input required name="full_name" value={setupCtx.data.full_name} onChange={setupCtx.onChange} className={inputCls}/></Field>
                  <Field label="Company Name"><input name="company" value={setupCtx.data.company} onChange={setupCtx.onChange} className={inputCls}/></Field>
                  <Field label="Country"><input name="country" value={setupCtx.data.country} onChange={setupCtx.onChange} className={inputCls}/></Field>
                  <Field label="Email Address"><input required type="email" name="email" value={setupCtx.data.email} onChange={setupCtx.onChange} className={inputCls}/></Field>
                  <Field label="Business Stage">
                    <select name="business_stage" value={setupCtx.data.business_stage} onChange={setupCtx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {contact.businessStages.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Support Type">
                    <select name="support_type" value={setupCtx.data.support_type} onChange={setupCtx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {contact.supportTypes.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred Market">
                    <select name="preferred_market" value={setupCtx.data.preferred_market} onChange={setupCtx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {contact.preferredMarkets.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Message"><textarea rows={4} name="message" value={setupCtx.data.message} onChange={setupCtx.onChange} className={inputCls + " resize-none"}/></Field>
                <MarketingCheckbox checked={setupCtx.data.marketing_consent} onChange={setupCtx.onChange}/>
                <PrivacyNotice/>
                <ButtonPrimary type="submit" data-testid="submit-setup">{setupCtx.loading ? "Sending…" : "Submit Setup Request"}</ButtonPrimary>
              </form>
            )}

            {mode === "call" && (
              <form onSubmit={callCtx.submit} className="space-y-5" data-testid="call-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name"><input required name="full_name" value={callCtx.data.full_name} onChange={callCtx.onChange} className={inputCls}/></Field>
                  <Field label="Company"><input name="company" value={callCtx.data.company} onChange={callCtx.onChange} className={inputCls}/></Field>
                  <Field label="Email"><input required type="email" name="email" value={callCtx.data.email} onChange={callCtx.onChange} className={inputCls}/></Field>
                  <Field label="Interest">
                    <select name="interest" value={callCtx.data.interest} onChange={callCtx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {contact.topics.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred Date & Time"><input name="preferred_datetime" value={callCtx.data.preferred_datetime} onChange={callCtx.onChange} className={inputCls}/></Field>
                </div>
                <Field label="Message"><textarea rows={4} name="message" value={callCtx.data.message} onChange={callCtx.onChange} className={inputCls + " resize-none"}/></Field>
                <MarketingCheckbox checked={callCtx.data.marketing_consent} onChange={callCtx.onChange}/>
                <PrivacyNotice/>
                <ButtonPrimary type="submit" data-testid="submit-call">{callCtx.loading ? "Sending…" : "Request Call"}</ButtonPrimary>
              </form>
            )}

            {mode === "partner" && (
              <form onSubmit={partnerCtx.submit} className="space-y-5" data-testid="partner-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name"><input required name="full_name" value={partnerCtx.data.full_name} onChange={partnerCtx.onChange} className={inputCls}/></Field>
                  <Field label="Organisation"><input required name="company" value={partnerCtx.data.company} onChange={partnerCtx.onChange} className={inputCls}/></Field>
                  <Field label="Country"><input name="country" value={partnerCtx.data.country} onChange={partnerCtx.onChange} className={inputCls}/></Field>
                  <Field label="Email"><input required type="email" name="email" value={partnerCtx.data.email} onChange={partnerCtx.onChange} className={inputCls}/></Field>
                  <Field label="Partner Type">
                    <select name="partner_type" value={partnerCtx.data.partner_type} onChange={partnerCtx.onChange} className={inputCls}>
                      <option value="">— Select —</option>
                      {["Accounting Firm","Advisory / Consulting","Legal / Certification","ERP / Software Provider","Financial Institution","Integration / Technology Partner","Other"].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="About your organisation"><textarea rows={4} name="message" value={partnerCtx.data.message} onChange={partnerCtx.onChange} className={inputCls + " resize-none"}/></Field>
                <MarketingCheckbox checked={partnerCtx.data.marketing_consent} onChange={partnerCtx.onChange}/>
                <PrivacyNotice/>
                <ButtonPrimary type="submit" data-testid="submit-partner">{partnerCtx.loading ? "Sending…" : "Submit Partner Application"}</ButtonPrimary>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
